import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getLocale, formatDateLong } from '../../../helpers/dates';
import { fetchShow } from '../../../actions/appActions';
import { getCookie, setCookie } from '../../../helpers/cookie';
import { SkeletonCommissions } from '../../Skeletons/SkeletonPoints';
import { useTranslation } from 'react-i18next';
import styles from './statusCommissions.module.css';
import { getCategory } from '../../../helpers/category';
import { WelletContext } from '../../../context/WelletContext';
import useGenericTranslation from '../../../helpers/translate';
import GeneralSelectorPicker from '../../SelectorPicker/GeneralSelectorPicker';

const StatusCommissions = ({ categoryName, title, lastUpdate }) => {
  const { t } = useTranslation();
  const { getClientTranslation } = useGenericTranslation();
  const app = useSelector((state) => state.app);
  const locale = getLocale(app.language);
  const [restaurants, setRestaurants] = useState(null);
  const [loadingRestaurants, setLoadingRestaurants] = useState(true);
  const [loading, setLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [commisions, setCommissions] = useState(null);
  const qualifyDateFormatted = formatDateLong(lastUpdate, locale);
  const welletContext = useContext(WelletContext);
  const restaurantCookie = getCookie('selectedRestaurant');

  const onRestaurantClick = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setCookie('selectedRestaurant', JSON.stringify(restaurant), 1440);
    getCommissions(restaurant.id);
  };

  const getRestaurants = async (selectedCity, page = 1, pageSize = 100) => {
    setLoadingRestaurants(true);
    try {
      const result = await fetchShow(app.language, selectedCity, page, pageSize, welletContext);
      const simplifiedRestaurants = result.shows.map((restaurant) => ({
        id: restaurant.id,
        name: restaurant.name,
      }));

      setRestaurants((prevData) => {
        if (prevData) {
          return [...prevData, ...simplifiedRestaurants];
        } else {
          return simplifiedRestaurants;
        }
      });

      setSelectedRestaurant(
        restaurantCookie !== '' ? JSON.parse(restaurantCookie) : simplifiedRestaurants[0],
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingRestaurants(false);
    }
  };

  const getCommissions = async (id = selectedRestaurant?.id) => {
    setLoading(true);
    try {
      const response = await welletContext.apis.tickets.get(`associates/getAllsalesCategory/${id}`);
      if (response.status === 200) {
        setCommissions(response.data.categoriesList.reverse());
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!restaurants) {
      getRestaurants();
    }
  }, []);

  useEffect(() => {
    if (restaurants) {
      getCommissions(selectedRestaurant.id);
    }
  }, [restaurants]);

  return (
    <div className={`card m-0 p-0`}>
      <div className='px-3 pt-3'>
        <div className={' bold'}>{title}</div>
        <div className=' text-secondary font-small'>
          {t('updatedOn')} {qualifyDateFormatted}
        </div>
        <div className={`${styles.lineFirst} row mt-2`}></div>
        <div className='row justify-content-between align-items-center mt-2 '>
          <div className='col '>
            <div className='text-secondary font-small bold'>
              {getClientTranslation('title.plural')}
            </div>
            {restaurants && (
              <GeneralSelectorPicker
                options={restaurants}
                selectedValue={selectedRestaurant}
                onChange={onRestaurantClick}
                placeholder={t('bankForm.selectType')}
                useFirstOptionAsPlaceholder={true}
                backgroundAndBorder={false}
              />
            )}
          </div>
          <div className='col-auto pl-0 text-secondary font-tiny fw-semibold'>
            {t('discounts')} *
          </div>
        </div>
      </div>
      {!loading && !loadingRestaurants
        ? commisions?.map((c, index) => (
            <div key={index} className='mx-1 fade-up'>
              <div className='row justify-content-between p-3 align-items-center'>
                <div
                  className={`col-8 d-flex align-items-center  ${
                    categoryName === c.name ? 'bold' : 'text-secondary bold'
                  }`}
                >
                  {getCategory(c.name, t)}
                  <span className={categoryName === c.name ? 'mb-1 ml-2' : 'd-none'}>
                    <div className={`${styles.dot} mt-1`} />
                  </span>
                </div>
                <div
                  className={`col-3 text-center ${
                    categoryName === c.name ? 'bold' : 'semi-bold text-secondary'
                  }`}
                >
                  {`${c.maxPercentDiscount}%`}
                </div>
              </div>
              {index !== commisions.length - 1 && <div className={`${styles.line}`}></div>}
            </div>
          ))
        : SkeletonCommissions()}
      <div className='card text-secondary px-3 py-2 font-small'>(*) {t('discountDescription')}</div>
    </div>
  );
};

export default StatusCommissions;
